/**
 * TaskDetailDialog — a task opened from the member's activity feed (TRUS-762).
 *
 * Read view first (title, status, due, priority, assignee, description, linked
 * case / program) with Complete and Edit. Edit swaps this dialog for TaskForm,
 * itself a Radix dialog, rather than stacking two modals.
 *
 * TaskDetailView is the same content without the dialog, for a host screen
 * that has its own header and way back (the member's Activities workspace
 * opens a task as a screen, not a modal). Edit there swaps to TaskForm's
 * in-page variant.
 */
import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, Pencil } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ACCENT_BUTTON } from '@/components/ui/accent';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '@/hooks/useUserTimezone';
import { getApiErrorMessage } from '@/utils/apiErrors';
import { formatDateOnly } from '@/utils/dateTime';
import { tasksAPI, PRIORITY_LABELS, STATUS_LABELS, type Task } from '@/services/api/tasks';
import TaskForm from './TaskForm';

export interface TaskDetailProps {
  taskId: number;
  memberId: number;
  /** "Case #12", from the feed row; the task API does not return the case number. */
  caseLabel?: string | null;
  programName?: string | null;
  onClose: () => void;
  /** The task changed (completed or edited). */
  onChanged: () => void;
}

type Props = TaskDetailProps;

export default function TaskDetailDialog(props: Props) {
  return <TaskDetailBody {...props} variant="dialog" />;
}

/** The task, in place (no dialog). The host draws the title and the way back. */
export function TaskDetailView(props: Props) {
  return <TaskDetailBody {...props} variant="page" />;
}

function TaskDetailBody({
  taskId, memberId, caseLabel, programName, onClose, onChanged, variant,
}: Props & { variant: 'dialog' | 'page' }) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setTask(await tasksAPI.getTask(taskId));
    } catch (e) {
      setError(getApiErrorMessage(e) || t('taskDetail.loadFailed', 'Could not load this task.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const complete = async () => {
    if (!task?.id) return;
    setCompleting(true);
    setError(null);
    try {
      const updated = await tasksAPI.updateTask(task.id, { status: 'completed' });
      setTask({ ...task, ...updated, status: 'completed' });
      onChanged();
    } catch (e) {
      setError(getApiErrorMessage(e) || t('taskDetail.completeFailed', 'Could not complete this task.'));
    } finally {
      setCompleting(false);
    }
  };

  if (editing && task) {
    return (
      <TaskForm
        memberId={memberId}
        task={task}
        variant={variant}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onChanged();
          void load();
        }}
      />
    );
  }

  const statusLabel = (s?: string) =>
    s ? t(`taskDetail.status.${s}`, STATUS_LABELS[s as keyof typeof STATUS_LABELS] || s.replace(/_/g, ' ')) : '—';
  const priorityLabel = (p?: string) =>
    p ? t(`taskDetail.priority.${p}`, PRIORITY_LABELS[p as keyof typeof PRIORITY_LABELS] || p) : '—';
  const due = task?.due_at ? fmt.dateTime(task.due_at) : task?.deadline ? formatDateOnly(task.deadline) : '—';
  const isDone = task?.status === 'completed' || task?.status === 'cancelled';
  const program = task?.program_name || programName;

  const row = (label: string, value: React.ReactNode) => (
    <div className="min-w-0">
      <div className="text-xs text-ink-500 mb-0.5">{label}</div>
      <div className="text-sm text-ink break-words">{value || '—'}</div>
    </div>
  );

  const content = (
      <>
        {loading ? (
          <div className="flex items-center gap-2 py-6 justify-center text-ink-500">
            <Loader2 className="w-4 h-4 animate-spin" /> {t('taskDetail.loading', 'Loading task…')}
          </div>
        ) : task ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {row(t('taskDetail.statusLabel', 'Status'), statusLabel(task.status))}
              {row(t('taskDetail.dueLabel', 'Due'), due)}
              {row(t('taskDetail.priorityLabel', 'Priority'), priorityLabel(task.priority))}
              {row(t('taskDetail.assigneeLabel', 'Assigned to'), task.assigned_to_name || t('taskDetail.unassigned', 'Unassigned'))}
              {(caseLabel || task.case_episode_id) &&
                row(t('taskDetail.caseLabel', 'Case'), caseLabel || `#${task.case_episode_id}`)}
              {program && row(t('taskDetail.programLabel', 'Program'), program)}
            </div>
            <div>
              <div className="text-xs text-ink-500 mb-0.5">{t('taskDetail.descriptionLabel', 'Description')}</div>
              <p className="text-sm text-ink whitespace-pre-wrap break-words">
                {task.description || t('taskDetail.noDescription', 'No description.')}
              </p>
            </div>
          </div>
        ) : null}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/30 dark:border-red-900 dark:text-red-300">
            {error}
          </div>
        )}

        {task && (
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="w-4 h-4 mr-1.5" />
              {t('taskDetail.edit', 'Edit')}
            </Button>
            {!isDone && (
              <Button size="sm" className={ACCENT_BUTTON} onClick={complete} disabled={completing}>
                {completing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                {t('taskDetail.complete', 'Complete')}
              </Button>
            )}
          </div>
        )}
      </>
  );

  if (variant === 'page') {
    return (
      <div className="rounded-lg border border-v2border bg-surface p-4 sm:p-6 space-y-4">
        {task?.title && <h3 className="text-base font-semibold text-ink">{task.title}</h3>}
        {content}
      </div>
    );
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg bg-surface border-v2border">
        <DialogHeader className="pr-8">
          <DialogTitle className="text-ink">
            {task?.title || t('taskDetail.title', 'Task')}
          </DialogTitle>
          <DialogDescription className="text-ink-500">
            {t('taskDetail.subtitle', 'Task details')}
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
